import { FC, type MouseEvent, useEffect, useState } from 'react';
import { DATE_FORMAT, formatValueForDateInput } from '@belong/date-utils';
import { ICON_NAMES } from '@belong/types';
import { TextInput } from '../TextInput';
import { CalendarIcon, ClearBtn, DateInputWrapper } from './DateInput.styles';
import type { IDateInput } from './DateInput.types';

/**
 * Use the controls available in the sidebar to configure <code>DateInput</code>
 *
 * The DateInput is a TextField with a formatting function that will format the input with slashes after achange event
 *
 * DateInput is intended to be used in the {'<FormInput />'} component but can be used directly
 */
export const DateInput: FC<IDateInput> = ({
  id,
  placeholder,
  name,
  onChange,
  onClear,
  value,
  format = DATE_FORMAT.INPUT,
  showCalendarIcon,
  showClearButton,
  setFieldValue,
  ...inputProps
}) => {
  const [prevValue, updatePrevValue] = useState('');
  const currentValue = value;

  useEffect(() => {
    const newValue = formatValueForDateInput({ prevValue, value: (currentValue || '') as string, format });

    if (newValue !== prevValue) {
      updatePrevValue(newValue);
    }

    if (setFieldValue && newValue !== currentValue) {
      setFieldValue(newValue);
    }
  }, [currentValue]);

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    if (setFieldValue) {
      setFieldValue('');
    }

    onClear?.(event);
  };

  return (
    <DateInputWrapper showClearButton={showClearButton}>
      <TextInput
        {...inputProps}
        name={name}
        onChange={onChange}
        id={id}
        value={value}
        placeholder={placeholder}
        type="text"
        inputMode="numeric"
      />
      {showCalendarIcon && <CalendarIcon name={ICON_NAMES.Calendar} size="small" />}
      {showClearButton && <ClearBtn onClick={handleClear}>Clear</ClearBtn>}
    </DateInputWrapper>
  );
};

DateInput.displayName = 'DateInput';
